import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Visibility,
  Receipt,
  ShoppingCart,
  LocalShipping,
  Schedule,
  CheckCircle,
  Download
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { ordersService, systemSettingsService } from '../services/api';
import { useCurrency } from '../context/CurrencyContext';
import { useSystemSettings } from '../context/SystemSettingsContext';
import jsPDF from 'jspdf';
import CelebrationAnimation from '../components/Common/CelebrationAnimation';

const OrdersPage = () => {
  const { isAuthenticated } = useAuth();
  const { formatPrice } = useCurrency();
  const { formatDateTime } = useSystemSettings();
  const navigate = useNavigate();
  const location = useLocation();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: ''
  });
  
  // System settings state
  const [systemSettings, setSystemSettings] = useState({
    gstPercentage: 5.0,
    taxPercentage: 0.0,
    enableGST: true,
    enableTax: false,
    taxMethod: 'inclusive'
  });
  
  // Modal state
  const [modal, setModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null
  });

  // Order details dialog state
  const [orderDetailsDialog, setOrderDetailsDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Celebration animation state
  const [showCelebration, setShowCelebration] = useState(false);

  const showModal = (type, title, message, onConfirm = null) => {
    setModal({
      isOpen: true,
      type,
      title,
      message,
      onConfirm
    });
  };

  const closeModal = () => {
    setModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleCelebrationComplete = () => {
    setShowCelebration(false);
    // Show success modal after celebration
    const orderNumber = location.state?.orderNumber;
    if (orderNumber) {
      showModal(
        'success',
        'Order Placed Successfully! 🎉',
        `Your order has been placed successfully!\n\nOrder Number: #${orderNumber}\n\nWe are currently working on integrating our payment gateway. For now, please visit our counter and mention your order number (#${orderNumber}) or your mobile number to our sales team. They will take your payment and start preparing your order.\n\nYou can track your order status on this page.\n\nSorry for the inconvenience. We are working to make this experience smooth and seamless as soon as possible.`
      );
    }
  };

  // Fetch system settings for tax configuration
  const fetchSystemSettings = async () => {
    try {
      console.log('[OrdersPage] Fetching system settings for tax configuration...');
      const response = await systemSettingsService.getGeneralSettings();
      
      if (response.data && response.data.success) {
        const settings = response.data.data;
        console.log('[OrdersPage] System settings loaded from API:', settings);
        setSystemSettings({
          gstPercentage: settings.gstPercentage || 5.0,
          taxPercentage: settings.taxPercentage || 0.0,
          enableGST: settings.enableGST !== undefined ? settings.enableGST : true,
          enableTax: settings.enableTax !== undefined ? settings.enableTax : false,
          taxMethod: settings.taxMethod || 'inclusive'
        });
      } else {
        console.log('[OrdersPage] API response unsuccessful, using defaults');
      }
    } catch (error) {
      console.error('[OrdersPage] Error fetching system settings (using defaults):', error.response?.status, error.message);
      // Keep default settings - API access denied for customers, which is normal
      console.log('[OrdersPage] Using default tax settings for customer view:', {
        gstPercentage: 5.0,
        taxPercentage: 0.0,
        enableGST: true,
        enableTax: false,
        taxMethod: 'inclusive'
      });
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await ordersService.getMyOrders();
      
      if (response.data && response.data.success) {
        setOrders(response.data.data || []);
      } else {
        throw new Error('Failed to fetch orders');
      }
      
    } catch (error) {
      console.error('Error fetching orders:', error);
      
      let errorMessage = 'Failed to load orders. Please try again later.';
      
      if (error.response?.status === 404) {
        errorMessage = 'Order tracking feature is currently being deployed. Please check back in a few minutes.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication expired. Please log in again to view your orders.';
      } else if (!navigator.onLine) {
        errorMessage = 'No internet connection. Please check your connection and try again.';
      }
      
      setError(errorMessage);
      
      const justOrdered = location.state?.fromCheckout;
      if (!justOrdered) {
        showModal('error', 'Unable to Load Orders', errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }

    // Show celebration animation and success message if coming from checkout
    const justOrdered = location.state?.fromCheckout;
    const orderNumber = location.state?.orderNumber;
    
    if (justOrdered && orderNumber) {
      // Show celebration animation first
      setShowCelebration(true);
    }

    // Fetch both system settings and orders
    fetchSystemSettings();
    fetchOrders();
  }, [isAuthenticated, navigate, location.state]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'preparing':
        return 'info';
      case 'ready_to_pick':
      case 'ready':
        return 'success';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return <Schedule />;
      case 'preparing':
        return <ShoppingCart />;
      case 'ready_to_pick':
      case 'ready':
        return <LocalShipping />;
      case 'completed':
        return <CheckCircle />;
      case 'cancelled':
        return '❌';
      default:
        return <Receipt />;
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Unknown';
    return formatDateTime(date) || 'Invalid Date';
  };

  const handleViewOrderDetails = (order) => {
    setSelectedOrder(order);
    setOrderDetailsDialog(true);
  };

  const handleCloseOrderDetails = () => {
    setOrderDetailsDialog(false);
    setSelectedOrder(null);
  };

  // Generate and download PDF invoice
  const downloadInvoice = (order) => {
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.width;
    const margin = 20;
    let yPosition = 30;

    // PDF-specific price formatting function (uses Rs instead of currency symbol)
    const formatPriceForPDF = (amount) => {
      const numAmount = parseFloat(amount) || 0;
      return `Rs ${numAmount.toFixed(2)}`;
    };

    // Helper function to add text with proper spacing
    const addText = (text, x, y, options = {}) => {
      pdf.setFontSize(options.fontSize || 10);
      pdf.setFont(options.font || 'helvetica', options.style || 'normal');
      if (options.color) {
        try {
          if (Array.isArray(options.color) && options.color.length === 3) {
            // Ensure color values are valid numbers between 0-255
            const r = Math.max(0, Math.min(255, parseInt(options.color[0]) || 0));
            const g = Math.max(0, Math.min(255, parseInt(options.color[1]) || 0));
            const b = Math.max(0, Math.min(255, parseInt(options.color[2]) || 0));
            pdf.setTextColor(r, g, b);
          } else if (typeof options.color === 'string') {
            pdf.setTextColor(options.color);
          } else if (typeof options.color === 'number') {
            pdf.setTextColor(options.color);
          }
        } catch (error) {
          console.warn('Invalid color format, using default:', options.color);
          pdf.setTextColor(0, 0, 0); // fallback to black
        }
      }
      // Ensure text is a string
      const textStr = String(text || '');
      pdf.text(textStr, x, y);
      if (options.color) pdf.setTextColor(0, 0, 0); // Reset to black
      return y + (options.lineHeight || 6);
    };

    // Calculate totals for PDF (using correct order total)
    const orderTotal = getCorrectOrderTotal(order);
    const subtotal = order.subtotal || orderTotal;
    const discountAmount = order.discountAmount || 0;
    const deliveryCharges = order.deliveryCharges || 0;
    
    // Tax breakdown calculation for PDF
    const gstPercentage = 5.0;
    const gstRate = gstPercentage / 100;
    const baseAmount = subtotal / (1 + gstRate);
    const gstAmount = subtotal - baseAmount;

    // Header
    pdf.setFillColor(233, 30, 99); // Pink header
    pdf.rect(0, 0, pageWidth, 25, 'F');
    
    addText('BUNNY BOBA - INVOICE', margin, 15, {
      fontSize: 16,
      style: 'bold',
      color: [255, 255, 255]
    });

    yPosition = 40;

    // Invoice details
    addText(`Invoice #${order.dailyOrderNumber || order.orderNumber || order.id}`, margin, yPosition, {
      fontSize: 14,
      style: 'bold'
    });
    yPosition += 8;
    
    addText(`Date: ${formatDate(order.createdAt)}`, margin, yPosition);
    yPosition += 15;

    // Customer Information
    addText('CUSTOMER INFORMATION', margin, yPosition, {
      fontSize: 12,
      style: 'bold'
    });
    yPosition += 8;
    
    addText(`Name: ${order.customerName || 'N/A'}`, margin, yPosition);
    yPosition += 6;
    addText(`Mobile: ${order.customerMobile || 'N/A'}`, margin, yPosition);
    yPosition += 6;
    addText(`Order Type: ${order.orderType?.toUpperCase() || 'IN-STORE'}`, margin, yPosition);
    yPosition += 15;

    // Order Items Header
    addText('ORDER ITEMS', margin, yPosition, {
      fontSize: 12,
      style: 'bold'
    });
    yPosition += 10;

    // Table headers
    const tableHeaders = ['Item', 'Size', 'Qty', 'Price', 'Total'];
    const colWidths = [80, 25, 20, 30, 30];
    let xPos = margin;
    
    pdf.setFillColor(240, 240, 240);
    pdf.rect(margin, yPosition - 5, pageWidth - 2 * margin, 8, 'F');
    
    tableHeaders.forEach((header, index) => {
      addText(header, xPos, yPosition, { style: 'bold' });
      xPos += colWidths[index];
    });
    yPosition += 10;

    // Order Items
    if (order.items && order.items.length > 0) {
      order.items.forEach((item, index) => {
        xPos = margin;
        
        // Item name
        addText(item.menuItemName || 'Unknown Item', xPos, yPosition);
        xPos += colWidths[0];
        
        // Size
        addText(item.size || 'Standard', xPos, yPosition);
        xPos += colWidths[1];
        
        // Quantity
        addText(String(item.quantity || 1), xPos, yPosition);
        xPos += colWidths[2];
        
        // Price
        addText(formatPriceForPDF(item.itemPrice || item.price || 0), xPos, yPosition);
        xPos += colWidths[3];
        
        // Total
        addText(formatPriceForPDF(item.totalItemPrice || item.total || 0), xPos, yPosition);
        
        yPosition += 6;
        
        // Toppings (if any)
        if (item.toppings && item.toppings.length > 0) {
          item.toppings.forEach(topping => {
            addText(`  + ${topping.name} (+${formatPriceForPDF(topping.price)})`, margin + 5, yPosition, {
              fontSize: 8,
              color: [100, 100, 100]
            });
            yPosition += 4;
          });
        }
        
        yPosition += 2;
      });
    }

    yPosition += 10;

    // Draw line
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 10;

    addText('PAYMENT SUMMARY', margin, yPosition, {
      fontSize: 12,
      style: 'bold'
    });
    yPosition += 10;

    // Summary items
    const summaryItems = [
      ['Subtotal (Base Amount):', formatPriceForPDF(baseAmount)],
      ['GST (5%):', formatPriceForPDF(gstAmount)],
      ['Total Tax (Included):', formatPriceForPDF(gstAmount)],
      ['Discount:', `-${formatPriceForPDF(discountAmount)}`],
      ['Delivery Charges:', formatPriceForPDF(deliveryCharges)]
    ];

    summaryItems.forEach(([label, value]) => {
      addText(label, margin, yPosition);
      addText(value, pageWidth - margin - 40, yPosition);
      yPosition += 6;
    });

    yPosition += 5;
    
    // Total Amount
    pdf.setFillColor(233, 30, 99);
    pdf.rect(margin, yPosition - 3, pageWidth - 2 * margin, 12, 'F');
    
    addText('TOTAL AMOUNT:', margin + 5, yPosition + 3, {
      fontSize: 12,
      style: 'bold',
      color: [255, 255, 255]
    });
    addText(formatPriceForPDF(orderTotal), pageWidth - margin - 40, yPosition + 3, {
      fontSize: 12,
      style: 'bold',
      color: [255, 255, 255]
    });

    yPosition += 20;

    // Footer
    addText('Tax Configuration: GST 5% - All taxes are included in menu prices', margin, yPosition, {
      fontSize: 8,
      color: [100, 100, 100]
    });
    yPosition += 8;
    
    addText('Thank you for choosing Bunny Boba!', margin, yPosition, {
      fontSize: 10,
      style: 'bold',
      color: [233, 30, 99]
    });

    // Save the PDF
    const fileName = `Bunny_Boba_Invoice_${order.dailyOrderNumber || order.id}.pdf`;
    pdf.save(fileName);
  };

  // Helper function to calculate correct display total for order list
  const getCorrectOrderTotal = (order) => {
    console.log(`[OrdersPage] Calculating total for order ${order.id}:`, {
      order: order,
      taxSettings: order.taxSettings,
      version: order.taxSettings?.version,
      storedTotal: order.totalAmount || order.total,
      subtotal: order.subtotal
    });

    const subtotal = order.subtotal || 0;
    const discountAmount = order.discountAmount || 0;
    const deliveryCharges = order.deliveryCharges || 0;
    const storedTotal = order.totalAmount || order.total || 0;
    
    console.log(`[OrdersPage] Order ${order.id} checking for tax calculation error:`, {
      subtotal,
      discountAmount,
      deliveryCharges,
      storedTotal,
      taxAmount: order.taxAmount
    });
    
    // Check if this looks like a tax-exclusive calculation error
    // This can happen even in v2.0 orders if backend processing is wrong
    if (subtotal > 0 && storedTotal > 0) {
      // If there's a taxAmount field and storedTotal = subtotal + taxAmount, it's wrong
      const taxAmount = order.taxAmount || 0;
      const expectedWrongTotal = subtotal + taxAmount;
      
      console.log(`[OrdersPage] Order ${order.id} tax check:`, {
        taxAmount,
        expectedWrongTotal,
        storedTotal,
        isWrongCalculation: Math.abs(storedTotal - expectedWrongTotal) < 1
      });
      
      // If stored total matches subtotal + taxAmount, it means tax was added incorrectly
      if (Math.abs(storedTotal - expectedWrongTotal) < 1 && taxAmount > 0) {
        const correctedTotal = subtotal - discountAmount + deliveryCharges;
        console.log(`[OrdersPage] Order ${order.id} CORRECTED - tax was added incorrectly:`, {
          storedTotal,
          correctedTotal,
          subtotal,
          taxAmount
        });
        return correctedTotal;
      }
      
      // Alternative check: if ratio is close to 1.05 (5% tax added)
      const ratio = storedTotal / subtotal;
      const expectedRatio = 1.05; // 5% tax added on top
      
      if (Math.abs(ratio - expectedRatio) < 0.02) {
        const correctedTotal = subtotal - discountAmount + deliveryCharges;
        console.log(`[OrdersPage] Order ${order.id} CORRECTED - ratio indicates tax-exclusive calculation:`, {
          storedTotal,
          correctedTotal,
          subtotal,
          ratio
        });
        return correctedTotal;
      }
    }
    
    // If no obvious correction needed, use stored total
    console.log(`[OrdersPage] Order ${order.id} using stored total (no correction needed):`, storedTotal);
    return storedTotal;
  };

  // Filter orders based on current filters
  const filteredOrders = orders.filter(order => {
    return (!filters.status || order.status === filters.status);
  });

  // Overview stats
  const completedOrders = orders.filter(o => o.status === 'completed').length;
  const activeOrders = orders.filter(o => ['pending', 'preparing', 'ready_to_pick', 'ready'].includes(o.status)).length;
  const totalSpent = orders.reduce((sum, o) => sum + getCorrectOrderTotal(o), 0);

  const renderOrderDetailsDialog = () => {
    if (!selectedOrder) return null;

    // Tax-inclusive calculation functions (EXACT copy from SalesCapture.js)
    const calculateTaxBreakdown = (totalPrice, taxSettings) => {
      if (!totalPrice || totalPrice <= 0) {
        return {
          totalPrice: 0,
          basePrice: 0,
          gstAmount: 0,
          taxAmount: 0,
          totalTaxAmount: 0
        };
      }

      const T = parseFloat(totalPrice); // Total Price (including all taxes)
      let gstAmount = 0;
      let taxAmount = 0;
      let totalTaxRate = 0;

      // Calculate total tax rate
      if (taxSettings.enableGST) {
        totalTaxRate += taxSettings.gstPercentage;
      }
      if (taxSettings.enableTax) {
        totalTaxRate += taxSettings.taxPercentage;
      }

      console.log('[OrdersPage] Tax calculation debug:', {
        totalPrice: T,
        taxSettings: taxSettings,
        totalTaxRate: totalTaxRate
      });

      if (totalTaxRate > 0) {
        // Using the formula: B = T / (1 + R) where R is total tax rate as decimal
        const R = totalTaxRate / 100; // Convert percentage to decimal
        const basePrice = T / (1 + R);

        // Calculate individual tax amounts
        if (taxSettings.enableGST) {
          const gstRate = taxSettings.gstPercentage / 100;
          gstAmount = T - (T / (1 + gstRate));
        }

        if (taxSettings.enableTax) {
          const taxRate = taxSettings.taxPercentage / 100;
          taxAmount = T - (T / (1 + taxRate));
        }

        // If both taxes are enabled, we need to calculate them properly
        if (taxSettings.enableGST && taxSettings.enableTax) {
          // For multiple taxes, we need to calculate the combined effect
          // Total tax amount from the combined rate
          const totalTaxAmount = T - basePrice;
          
          // Distribute the total tax proportionally
          const gstProportion = taxSettings.gstPercentage / totalTaxRate;
          const taxProportion = taxSettings.taxPercentage / totalTaxRate;
          
          gstAmount = totalTaxAmount * gstProportion;
          taxAmount = totalTaxAmount * taxProportion;
        }

        const totalTaxAmount = gstAmount + taxAmount;

        console.log('[OrdersPage] Tax breakdown result:', {
          totalPrice: T,
          basePrice,
          gstAmount,
          taxAmount,
          totalTaxAmount
        });

        return {
          totalPrice: T,
          basePrice: basePrice,
          gstAmount: gstAmount,
          taxAmount: taxAmount,
          totalTaxAmount: totalTaxAmount
        };
      } else {
        // No taxes enabled, total price is the base price
        return {
          totalPrice: T,
          basePrice: T,
          gstAmount: 0,
          taxAmount: 0,
          totalTaxAmount: 0
        };
      }
    };

    // Calculate order totals (EXACT copy from SalesCapture.js logic)
    const calculateOrderTotals = () => {
      const subtotal = selectedOrder.subtotal || selectedOrder.totalAmount || 0;
      const discountAmount = Number(selectedOrder.discountAmount) || 0;
      const deliveryCharges = Number(selectedOrder.deliveryCharges) || 0;
      
      // Priority order for tax settings:
      // 1. Order's stored tax settings (most accurate)
      // 2. Default 5% GST if no settings found
      let orderTaxSettings = null;
      
      if (selectedOrder.taxSettings && selectedOrder.taxSettings.gstPercentage !== undefined) {
        orderTaxSettings = selectedOrder.taxSettings;
        console.log('[OrdersPage] Using order stored tax settings:', orderTaxSettings);
      } else {
        // Fallback to 5% GST for orders without stored tax settings
        orderTaxSettings = {
          gstPercentage: 5.0,
          taxPercentage: 0.0,
          enableGST: true,
          enableTax: false,
          taxMethod: 'inclusive'
        };
        console.log('[OrdersPage] Order has no stored tax settings, using 5% GST fallback:', orderTaxSettings);
      }
      
      console.log('[OrdersPage] Order totals calculation:', {
        subtotal,
        discountAmount,
        deliveryCharges,
        orderTaxSettings,
        selectedOrderTaxSettings: selectedOrder.taxSettings
      });
      
      // For tax-inclusive system, the subtotal already includes all taxes
      // We need to calculate the breakdown for display purposes
      const taxBreakdown = calculateTaxBreakdown(subtotal, orderTaxSettings);
      
      // Calculate final total (subtotal already includes taxes, so we just add/subtract other charges)
      const finalTotal = subtotal - discountAmount + deliveryCharges;
      
      // Calculate tax breakdown for the final total (after discount and delivery)
      const finalTaxBreakdown = calculateTaxBreakdown(finalTotal, orderTaxSettings);

      console.log('[OrdersPage] Tax-inclusive calculation result:', {
        subtotal,
        discountAmount,
        deliveryCharges,
        orderTaxSettings,
        taxBreakdown,
        finalTotal,
        finalTaxBreakdown
      });

      return {
        subtotal: subtotal.toFixed(2),
        baseSubtotal: taxBreakdown.basePrice.toFixed(2),
        subtotalGstAmount: taxBreakdown.gstAmount.toFixed(2),
        subtotalTaxAmount: taxBreakdown.taxAmount.toFixed(2),
        subtotalTotalTaxAmount: taxBreakdown.totalTaxAmount.toFixed(2),
        discountAmount: discountAmount.toFixed(2),
        deliveryCharges: deliveryCharges.toFixed(2),
        finalBaseAmount: finalTaxBreakdown.basePrice.toFixed(2),
        gstAmount: finalTaxBreakdown.gstAmount.toFixed(2),
        taxAmount: finalTaxBreakdown.taxAmount.toFixed(2),
        totalTaxAmount: finalTaxBreakdown.totalTaxAmount.toFixed(2),
        totalAmount: finalTotal.toFixed(2),
        taxSettings: orderTaxSettings
      };
    };

    // Calculate all the totals
    const totals = calculateOrderTotals();

    return (
      <Dialog 
        open={orderDetailsDialog} 
        onClose={handleCloseOrderDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Receipt sx={{ mr: 1 }} />
            Order #{selectedOrder.dailyOrderNumber || selectedOrder.orderNumber || selectedOrder.id}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            {/* Order Information */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Order Information</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="Order Number" 
                      secondary={`#${selectedOrder.dailyOrderNumber || selectedOrder.orderNumber || selectedOrder.id}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Order Type" 
                      secondary={selectedOrder.orderType?.toUpperCase() || 'IN-STORE'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Status" 
                      secondary={
                        <Chip 
                          label={selectedOrder.status?.replace('_', ' ').toUpperCase() || 'PENDING'} 
                          size="small"
                          color={getStatusColor(selectedOrder.status)}
                          icon={getStatusIcon(selectedOrder.status)}
                        />
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Order Date" 
                      secondary={formatDate(selectedOrder.createdAt)}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Payment Status" 
                      secondary={
                        <Chip 
                          label={selectedOrder.paymentStatus?.toUpperCase() || 'PENDING'} 
                          size="small"
                          color={selectedOrder.paymentStatus === 'received' ? 'success' : 'warning'}
                        />
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            {/* Payment Summary - EXACT format from SalesManagement.js */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Payment Summary</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="Subtotal" 
                      secondary={
                        <Box component="div">
                          <Typography variant="body2" component="span">
                            {formatPrice(totals.baseSubtotal)}
                          </Typography>
                          {totals.taxSettings?.taxMethod === 'inclusive' && (
                            <Typography variant="caption" color="text.secondary" component="div">
                              Base price (before taxes)
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {/* Comprehensive Tax Breakdown */}
                  {parseFloat(totals.subtotalGstAmount) > 0 && (
                    <ListItem>
                      <ListItemText 
                        primary={`GST (${totals.taxSettings?.gstPercentage || 5}%)`}
                        secondary={formatPrice(totals.subtotalGstAmount)}
                      />
                    </ListItem>
                  )}
                  {parseFloat(totals.subtotalTaxAmount) > 0 && (
                    <ListItem>
                      <ListItemText 
                        primary={`Additional Tax (${totals.taxSettings?.taxPercentage || 0}%)`}
                        secondary={formatPrice(totals.subtotalTaxAmount)}
                      />
                    </ListItem>
                  )}
                  {parseFloat(totals.subtotalTotalTaxAmount) > 0 && (
                    <ListItem>
                      <ListItemText 
                        primary={
                          <Typography variant="body2" fontWeight="medium">
                            Total Tax Amount (inclusive)
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" fontWeight="medium" color="primary">
                            {formatPrice(totals.subtotalTotalTaxAmount)}
                          </Typography>
                        }
                      />
                    </ListItem>
                  )}
                  <ListItem>
                    <ListItemText 
                      primary="Discount" 
                      secondary={`-${formatPrice(totals.discountAmount)}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Delivery Charges" 
                      secondary={formatPrice(totals.deliveryCharges)}
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary={<Typography variant="h6">Total Amount</Typography>}
                      secondary={
                        <Typography variant="h6" sx={{ color: '#E91E63' }}>
                          {formatPrice(totals.totalAmount)}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            {/* Order Items */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Order Items</Typography>
                {selectedOrder.items && selectedOrder.items.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Item</TableCell>
                          <TableCell>Size</TableCell>
                          <TableCell>Quantity</TableCell>
                          <TableCell>Toppings</TableCell>
                          <TableCell align="right">Price (Tax Incl.)</TableCell>
                          <TableCell align="right">Total (Tax Incl.)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedOrder.items.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {item.menuItemName || item.name || 'Unknown Item'}
                              </Typography>
                            </TableCell>
                            <TableCell>{item.size || 'Large'}</TableCell>
                            <TableCell>{item.quantity || 1}</TableCell>
                            <TableCell>
                              {item.toppings && item.toppings.length > 0 ? (
                                <Box>
                                  {item.toppings.map((topping, tIndex) => (
                                    <Chip 
                                      key={tIndex}
                                      label={`${topping.name} (+${formatPrice(topping.price)})`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ mr: 0.5, mb: 0.5 }}
                                    />
                                  ))}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">None</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {formatPrice(item.itemPrice || item.price || 0)}
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight="medium">
                                {formatPrice(item.totalItemPrice || item.total || 0)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No items found for this order
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* Notes */}
            {selectedOrder.notes && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Order Notes</Typography>
                  <Typography variant="body2">{selectedOrder.notes}</Typography>
                </Paper>
              </Grid>
            )}

            {/* Payment Instructions */}
            {selectedOrder.paymentStatus === 'pending' && (
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Payment Pending:</strong> Please visit our store with your order number or mobile number to complete the payment and collect your order.
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOrderDetails}>Close</Button>
          {selectedOrder.status?.toLowerCase() === 'completed' && selectedOrder.paymentStatus?.toLowerCase() === 'completed' ? (
            <Button onClick={() => downloadInvoice(selectedOrder)} startIcon={<Download />}>
              Download Invoice
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ mr: 2, fontStyle: 'italic' }}>
              Invoice available after order and payment completion
            </Typography>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2, color: '#E91E63' }} />
          <Typography variant="h6" color="text.secondary">Loading your orders...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, minHeight: '80vh', bgcolor: '#f5f5f5' }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
            My Orders
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track your order history and current status
          </Typography>
        </Box>

        {/* Overview Cards */}
        {orders.length > 0 && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <ShoppingCart sx={{ fontSize: 40, color: '#E91E63', mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold" sx={{ color: '#E91E63' }}>
                    {orders.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Orders
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {completedOrders}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Schedule sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {activeOrders}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Orders
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Receipt sx={{ fontSize: 40, color: '#9C27B0', mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold" sx={{ color: '#9C27B0' }}>
                    {formatPrice(totalSpent)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Spent
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        {orders.length > 0 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Typography variant="h6">Filter Orders</Typography>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Filter by Status"
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="preparing">Preparing</MenuItem>
                  <MenuItem value="ready_to_pick">Ready to Pick</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                Showing {filteredOrders.length} of {orders.length} orders
              </Typography>
            </Box>
          </Paper>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={fetchOrders}>
                Try Again
              </Button>
            </Box>
          </Alert>
        )}

        {/* Empty State */}
        {!error && filteredOrders.length === 0 && (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <ShoppingCart sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom color="text.secondary">
              {orders.length === 0 ? 'No Orders Yet' : 'No Orders Match Filters'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {orders.length === 0 
                ? "You haven't placed any orders yet. Start by exploring our delicious menu!"
                : "Try adjusting your filters to see more orders."
              }
            </Typography>
            {orders.length === 0 && (
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/menu')}
                sx={{ 
                  mt: 2,
                  background: 'linear-gradient(45deg, #E91E63, #FF1493)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #C2185B, #E91E63)',
                  }
                }}
              >
                Browse Menu
              </Button>
            )}
          </Paper>
        )}

        {/* Orders Table */}
        {!error && filteredOrders.length > 0 && (
          <Paper sx={{ overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ 
                    background: 'linear-gradient(45deg, #E91E63, #FF1493)',
                  }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Order ID</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date & Time</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Items</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Total</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Payment</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOrders.map((order, index) => (
                    <TableRow 
                      key={order.id}
                      hover
                      sx={{ 
                        '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                        cursor: 'pointer'
                      }}
                      onClick={() => handleViewOrderDetails(order)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Receipt sx={{ mr: 1, color: '#E91E63' }} />
                          <Typography variant="body2" fontWeight="bold" sx={{ color: '#E91E63' }}>
                            #{order.dailyOrderNumber || order.orderNumber || order.id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {formatDate(order.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? 's' : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {order.items?.slice(0, 1).map(item => item.menuItemName).join(', ')}
                            {order.items && order.items.length > 1 && '...'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight="bold" sx={{ color: '#E91E63' }}>
                          {formatPrice(getCorrectOrderTotal(order))}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={order.status?.replace('_', ' ').toUpperCase() || 'PENDING'} 
                          size="small"
                          color={getStatusColor(order.status)}
                          icon={getStatusIcon(order.status)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={order.paymentStatus?.toUpperCase() || 'PENDING'} 
                          size="small"
                          color={order.paymentStatus === 'received' ? 'success' : 'warning'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          sx={{ color: '#E91E63' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewOrderDetails(order);
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Order Details Dialog */}
        {renderOrderDetailsDialog()}

        {/* Simple Modal for notifications */}
        <Dialog open={modal.isOpen} onClose={closeModal}>
          <DialogTitle>{modal.title}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {modal.message}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeModal}>OK</Button>
          </DialogActions>
        </Dialog>

        {/* Celebration Animation */}
        <CelebrationAnimation 
          show={showCelebration} 
          onComplete={handleCelebrationComplete}
        />
      </Box>
    </Box>
  );
};

export default OrdersPage; 